import {
  CampaignPerformanceSourceItem,
  CampaignPerformanceSourcesResponse,
  PerformanceAudienceFilterOption,
} from '@/services/endpoints/performanceAudience';

type PlatformKey = 'google' | 'meta';

const getCountLike = (item: CampaignPerformanceSourceItem, keys: Array<keyof CampaignPerformanceSourceItem>): number | undefined => {
  for (const key of keys) {
    const v = item[key];
    if (typeof v === 'number' && Number.isFinite(v)) {
      return v;
    }
  }

  return undefined;
};

const getIdLike = (item: CampaignPerformanceSourceItem): string | undefined => {
  const id =
    item.id ??
    item.sourceId ??
    (item as { externalAccountId?: string }).externalAccountId ??
    (item as { metaId?: string }).metaId ??
    (item as { googleId?: string }).googleId ??
    (item as { key?: string }).key;
  return id ? String(id) : undefined;
};

const getValueLike = (item: CampaignPerformanceSourceItem): string | undefined => {
  return (
    item.name ??
    item.label ??
    item.value ??
    (item as { title?: string }).title ??
    (item as { externalAccountId?: string }).externalAccountId
  );
};

const getFlatSources = (response: CampaignPerformanceSourcesResponse | undefined): any[] => {
  if (!response || typeof response !== 'object') {
    return [];
  }

  const sources = (response as { sources?: unknown }).sources;
  return Array.isArray(sources) ? sources : [];
};

const getItemsForPlatform = (response: CampaignPerformanceSourcesResponse | undefined, platform: PlatformKey): any[] => {
  if (!response) {
    return [];
  }

  const flatSources = getFlatSources(response).filter((item) => item?.platform === platform);
  if (flatSources.length) {
    return flatSources;
  }

  const rawPlatform = (response as Record<string, unknown>)?.[platform];

  if (Array.isArray(rawPlatform)) {
    return rawPlatform;
  }

  // Common wrapper patterns: { items: [...] } | { sources: [...] } | { data: [...] }
  if (rawPlatform && typeof rawPlatform === 'object') {
    const maybeItems =
      (rawPlatform as { items?: unknown }).items ??
      (rawPlatform as { sources?: unknown }).sources ??
      (rawPlatform as { data?: unknown }).data ??
      (rawPlatform as { list?: unknown }).list;
    if (Array.isArray(maybeItems)) {
      return maybeItems;
    }
  }

  return [];
};

const mapItemsToFilterOptions = (
  items: any[],
  platform: PlatformKey,
  fallback: PerformanceAudienceFilterOption[]
): PerformanceAudienceFilterOption[] => {
  const mapped = items
    .map((item: any): PerformanceAudienceFilterOption | null => {
      const id = getIdLike(item as CampaignPerformanceSourceItem);
      const value = getValueLike(item as CampaignPerformanceSourceItem);

      if (!id || !value) {
        return null;
      }

      const count =
        (item as CampaignPerformanceSourceItem).suffix
          ? undefined
          : getCountLike(item as CampaignPerformanceSourceItem, ['count', 'accountCount', 'adAccountCount', 'accountsCount']);

      const suffix =
        typeof item?.suffix === 'string'
          ? item.suffix
          : typeof count === 'number'
            ? `· ${count} ${platform === 'meta' ? 'ad account' : 'account'}`
            : undefined;

      const menuLabel =
        typeof item?.menuLabel === 'string'
          ? item.menuLabel
          : [value, suffix].filter(Boolean).join(' ');

      return {
        id,
        value: String(value),
        suffix,
        menuLabel,
      };
    })
    .filter((x): x is PerformanceAudienceFilterOption => x !== null);

  return mapped.length ? mapped : fallback;
};

export const mapCampaignPerformanceSourcesToFilterOptions = (
  response: CampaignPerformanceSourcesResponse | undefined,
  fallback: { googleScopes: PerformanceAudienceFilterOption[]; metaScopes: PerformanceAudienceFilterOption[] }
): { googleScopes: PerformanceAudienceFilterOption[]; metaScopes: PerformanceAudienceFilterOption[] } => {
  const googleItems = getItemsForPlatform(response, 'google');
  const metaItems = getItemsForPlatform(response, 'meta');

  return {
    googleScopes: mapItemsToFilterOptions(googleItems, 'google', fallback.googleScopes),
    metaScopes: mapItemsToFilterOptions(metaItems, 'meta', fallback.metaScopes),
  };
};
